import { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, deleteDoc, limit, where, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage real-time notifications for the current user
 * For header dropdown: loads only 5 latest unread notifications
 * For full page: loads all notifications with pagination
 */
export const useNotifications = (isFullPage = false) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      const notificationsRef = collection(db, 'users', user.uid, 'notifications');
      
      // For header: only load 5 latest unread notifications
      if (!isFullPage) {
        const q = query(
          notificationsRef,
          where('read', '==', false),
          orderBy('createdAt', 'desc'),
          limit(5)
        );

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const notifs = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setNotifications(notifs);
            setLoading(false);
            console.log('[useNotifications] Updated unread notifications:', notifs.length, notifs);
          },
          (err) => {
            console.error('[useNotifications] Error listening to unread notifications:', err);
            console.error('[useNotifications] Error code:', err.code);
            console.error('[useNotifications] Error message:', err.message);
            setError(err.message);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } else {
        // For full page: load all notifications with pagination
        const q = query(notificationsRef, orderBy('createdAt', 'desc'), limit(10));

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const notifs = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setNotifications(notifs);
            setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setHasMore(snapshot.docs.length === 10);
            setLoading(false);
            console.log('[useNotifications] Loaded notifications:', notifs.length);
          },
          (err) => {
            console.error('[useNotifications] Error listening to notifications:', err);
            setError(err.message);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      }
    } catch (err) {
      console.error('[useNotifications] Setup error:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [user?.uid, isFullPage]);

  /**
   * Load more notifications for full page (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!user?.uid || !lastDoc || !hasMore) return;

    try {
      const notificationsRef = collection(db, 'users', user.uid, 'notifications');
      const q = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(10)
      );

      const snapshot = await getDocs(q);
      const newNotifs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setNotifications((prev) => [...prev, ...newNotifs]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === 10);
      console.log('[useNotifications] Loaded more notifications:', newNotifs.length);
    } catch (err) {
      console.error('[useNotifications] Error loading more:', err);
    }
  }, [user?.uid, lastDoc, hasMore]);

  /**
   * Mark a notification as read
   */
  const markAsRead = async (notificationId) => {
    if (!user?.uid) return;
    try {
      const notifRef = doc(db, 'users', user.uid, 'notifications', notificationId);
      await updateDoc(notifRef, { read: true });
    } catch (err) {
      console.error('[useNotifications] Error marking as read:', err);
    }
  };

  /**
   * Delete a notification
   */
  const deleteNotification = async (notificationId) => {
    if (!user?.uid) return;
    try {
      const notifRef = doc(db, 'users', user.uid, 'notifications', notificationId);
      await deleteDoc(notifRef);
    } catch (err) {
      console.error('[useNotifications] Error deleting notification:', err);
    }
  };

  /**
   * Get count of unread notifications
   */
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    loading,
    error,
    unreadCount,
    hasMore,
    loadMore,
    markAsRead,
    deleteNotification,
  };
};
